import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi, googleAuthApi, referralsApi, eventsApi } from '../services/api';

/**
 * AuthContext — unified store for the two auth flows:
 *   1. Email/password (JWT) — backend sets an httpOnly `access_token` cookie.
 *   2. Google OAuth — backend sets an httpOnly `session_token` cookie.
 *
 * The frontend NEVER handles raw tokens. All API calls run with
 * `credentials: 'include'` so the correct cookie flows automatically.
 */

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing auth on mount. Tries both Google session and JWT
  // cookie-based /auth/profile. Either succeeding is enough.
  const checkAuth = useCallback(async () => {
    try {
      const googleUser = await googleAuthApi.getCurrentUser();
      setUser(googleUser);
      return;
    } catch (err) {
      // No Google session — try JWT next. Debug-log so this isn't silent.
      console.debug('[auth] no google session:', err?.status || err?.message);
    }
    try {
      const profile = await authApi.getProfile();
      setUser(profile);
    } catch (err) {
      // Fully unauthenticated — leave user null (expected for anonymous).
      console.debug('[auth] no JWT profile:', err?.status || err?.message);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and then push the user into
    // context via setGoogleUser().
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const initAuth = async () => {
      await checkAuth();
      setLoading(false);
    };
    initAuth();
  }, [checkAuth]);

  // session_end instrumentation — fire on tab/window close via sendBeacon
  // (the only delivery method that survives `beforeunload`). Tracks the
  // session duration since the user first authenticated in this tab.
  const sessionStartRef = useRef(null);
  useEffect(() => {
    if (user && !sessionStartRef.current) {
      sessionStartRef.current = Date.now();
    }
    const handler = () => {
      if (!sessionStartRef.current) return;
      const duration = Math.floor((Date.now() - sessionStartRef.current) / 1000);
      // `eventsApi.track` picks sendBeacon for session_end automatically.
      eventsApi.track('session_end', {
        duration_seconds: duration,
        sandbox_used: !!window.__sandboxUsedThisSession,
        tasks_completed: window.__tasksCompletedThisSession || 0,
      });
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [user]);

  // Email/Password login — backend sets httpOnly `access_token` cookie
  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await authApi.login({ email, password });
      setUser(response.user);
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Email/Password register
  const register = useCallback(async (username, email, password, referralCode = null) => {
    setError(null);
    try {
      const response = await authApi.register({ username, email, password });
      setUser(response.user);
      const trimmed = (referralCode || '').trim();
      if (trimmed) {
        try {
          await referralsApi.apply(trimmed);
        } catch (e) {
          console.warn('Referral apply failed:', e?.message);
        }
      }
      return response;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Google OAuth login - redirects to Google
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = useCallback(() => {
    googleAuthApi.initiateLogin();
  }, []);

  // Set user from Google OAuth callback
  const setGoogleUser = useCallback((userData) => {
    setUser(userData);
  }, []);

  // Logout — clears BOTH auth cookies (JWT + Google session). Each endpoint is
  // idempotent and safe to call even if that cookie wasn't set.
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (err) {
      console.debug('[auth] jwt logout ignored:', err?.message);
    }
    try {
      await googleAuthApi.logout();
    } catch (err) {
      console.debug('[auth] google logout ignored:', err?.message);
    }
    setUser(null);
  }, []);

  // Refresh user profile
  const refreshProfile = useCallback(async () => {
    try {
      const googleUser = await googleAuthApi.getCurrentUser();
      setUser(googleUser);
      return googleUser;
    } catch (err) {
      console.debug('[auth] refresh google failed, trying JWT:', err?.message);
      const profile = await authApi.getProfile();
      setUser(profile);
      return profile;
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    tier: user?.tier || 'free',
    isPro: (user?.tier || 'free') === 'pro',
    proExpiresAt: user?.proExpiresAt || null,
    trialUsed: !!user?.trialUsed,
    isTrial: !!user?.isTrial,
    login,
    register,
    loginWithGoogle,
    setGoogleUser,
    logout,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
